import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Sidebar, SidebarContent, SidebarHeader, useSidebar } from 'components/ui/sidebar';
import { X } from 'lucide-react';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { SidebarHeaderConfig, SidebarHeaderVariant } from '../../../pages/main/main-layout';
import { useChatStore } from '../../../state/store/chat/use-chat-store';
import { deleteConversationById } from '../../../services/storage-service';
import { useToast } from '../../../hooks/use-toast';
import { CustomDialog } from '../custom-dialog/custom-dialog';
import { hasValidConversationComponentRoute, hasValidPlanForStartViaShareLink } from 'pages/services/utilityService';
import { useTranslation } from 'react-i18next';
import { DebouncedInput } from '../debounced-input/debounced-input';
import { useConfig } from 'hooks/use-config';
import { useSubscriptionStore } from 'state/store/subscription';
import { SidebarGroupedSections } from './sidebar-grouped-sections';
import { AgentGroupBy, readAgentGroupBy } from './agents-group-menu';
import { ProjectActionDialogs } from 'features/projects/components/project-actions';
import { modalOption } from '/models/sidebar';
import { refetchProject } from 'features/projects/helpers/query-utils';
import { NewProjectModal } from 'features/projects/components/new-project-modal';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateProject } from 'features/projects/hooks/use-create-project';
import { PLANS } from 'constant/plan';
import { cn } from 'lib/utils';
import { DndContext, PointerSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';

import { useTheme } from 'components/core/theme-provider';
import { ConversationContextMenu } from './conversation-context-menu';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { IndividualChatsSection } from 'features/chat/components/individual-chats-section';
import { BuddyChatsSection } from 'features/chat/components/buddy-chats-section';
import { GroupChatsSection } from 'features/chat/components/group-chats-section';
import { AppSidebarFooter } from './sidebar-footer';
import { SidebarPrimaryNavItem } from './sidebar-primary-nav-item';
import { SidebarConfigurePanel } from './sidebar-configure-panel';
import { ShowChatsPanelButton, SidebarChatsPanel } from './sidebar-chats-panel';
import { isConfigureRoute } from 'constant/configure-nav';
// import { MyAssistantsSection } from 'features/chat/components/my-assistants-section';
import { useGetAgents } from 'features/chat/hooks/use-agents';
import { isDashboardEnabled, isGroupChatEnabled } from 'config/feature-flags';
import { isCatalogueAgentId } from 'utils/constants/chat-constants';
import {
  markConversationDeletedLocally,
  clearLocallyDeletedConversation,
  removeAgentConversationFromCache,
} from 'features/chat/utils/agent-sidebar-sync';

import { useGroupShareModal } from 'hooks/use-group-share-modal';
import { GroupShareModal } from 'features/chat/components/GroupShareModal';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { useAgentSelectionStore } from 'state/store/chat/use-agent-selection-store';
import { sendInvitation } from 'features/group-chat/services/group-services';
import seliseLogo from 'assets/images/selise-ai-logo-new.svg';
import seliseLogoDark from 'assets/images/selise-ai-logo-new-dark.svg';
import { isPathActive } from 'utils/is-path-active';
import { SidebarSettingsPanel, isSettingsRoute, SETTINGS_ACCOUNT_PATH } from './sidebar-settings-panel';


export function LogoSidebarHeader({
  open,
  isMobile,
  setOpenMobile,
}: SidebarHeaderConfig) {
  const { theme } = useTheme();
  return (
    <div className="relative h-10 w-full">
      <img
        src={theme === 'dark' ? seliseLogoDark : seliseLogo}
        alt="logo"
        className={`absolute left-4 top-1 h-10 w-auto max-w-full transition-all duration-300 ${open || isMobile ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
      />

      <img
        src={theme === 'dark' ? seliseLogoDark : seliseLogo}
        alt="smallLogo"
        className={`absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 h-8 w-8 transition-all duration-300 ${open || isMobile ? 'opacity-0 pointer-events-none' : 'opacity-100'
          }`}
      />

      {isMobile && (
        <button
          className="absolute right-4 top-1/2 transform -translate-y-1/2"
          onClick={setOpenMobile ? () => setOpenMobile(false) : undefined}
          aria-label="Close sidebar"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
}

export function AppSidebar({ type, logo, smallLogo }: SidebarHeaderConfig) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { pathname } = useLocation();
  const { conversationId: conversationIdFromParams, chatId: agentChatIdFromParams } =
    useParams<{ conversationId?: string; chatId?: string }>();
  const activeSidebarChatId = conversationIdFromParams ?? agentChatIdFromParams;
  const queryClient = useQueryClient();
  const { mutateAsync: updateProject } = useUpdateProject();
  const {
    setOpenMobile,
    isMobile,
    isSidebarOverlay,
    isConfigureInline,
    configureOpen,
    setConfigureOpen,
    setOpen,
    setChatsPanelOpen,
  } = useSidebar();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [conversationState, setConversationState] = useState<{
    id: string | null;
    type: 'delete' | 'archive' | 'rename' | 'leave' | null;
  }>({
    id: null,
    type: null,
  });
  // Drag and drop states
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [activeConversation, setActiveConversation] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const [individualPage, setIndividualPage] = useState(0);
  const [groupPage, setGroupPage] = useState(0);
  const [agentGroupBy, setAgentGroupBy] = useState<AgentGroupBy>(readAgentGroupBy);
  const pageSize = 50;
  const flatConversationList = useChatStore((state) => state.sidebarConversationsList);
  const totalIndividualConversationsCount = useChatStore((state) => state.totalIndividualConversationsCount);
  const totalGroupConversationsCount = useChatStore((state) => state.totalGroupConversationsCount);

  const individualChats = flatConversationList.filter((c) => !c.IsGroup);
  const groupChats = flatConversationList.filter((c) => c.IsGroup && !c.AgentId);

  const hasMoreIndividualChats = individualChats.length < totalIndividualConversationsCount;
  const hasMoreGroupChats = groupChats.length < totalGroupConversationsCount;
  const fetchSidebarConversations = useChatStore((state) => state.fetchSidebarConversations);
  const sidebarProjectsList = useChatStore((state) => state.sidebarProjectsList);
  const fetchAndUpdateProjectAllConversations = useChatStore(
    (state) => state.fetchAndUpdateProjectAllConversations
  );
  const isSidebarLoading = useChatStore((state) => state.isSidebarLoading);
  const isSidebarIndividualLoading = useChatStore((state) => state.isSidebarIndividualLoading);
  const isSidebarGroupLoading = useChatStore((state) => state.isSidebarGroupLoading);
  const { setIsIncognitoChat } = useChatStore();
  const removeConversationFromRecentAndProjects = useChatStore(
    (state) => state.removeConversationFromRecentAndProjects
  );
  const updateConversationTitleInStore = useChatStore(
    (state) => state.updateConversationTitleInStore
  );
  const shareTokens = useChatStore((state) => state.shareTokens);
  const user = useCommonStore((state) => state.user);
  const clearUserSelection = useAgentSelectionStore((state) => state.clearUserSelection);
  const { configurationSettings } = useConfig();

  const { currentPlan } = useSubscriptionStore();

  const { data: agentsData } = useGetAgents(
    {
      limit: 100,
      offset: 0,
      project_key: configurationSettings?.blocksKey || '',
      show_hidden_agent: false,
    },
    { scope: 'sidebar' },
  );

  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isRenameLoading, setIsRenameLoading] = useState(false);
  const [isAddProjectModalOpen, setIsAddProjectModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [conversationIdToAddToProject, setConversationIdToAddToProject] = useState<
    string | undefined
  >(undefined);
  const theme = useTheme();
  const [selectedConversationIdForShare, setSelectedConversationIdForShare] = useState<
    string | undefined
  >(undefined);
  // Configure DnD sensors
  const dragTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  useEffect(() => {
    return () => {
      if (dragTimeoutRef.current) {
        clearTimeout(dragTimeoutRef.current);
      }
    };
  }, []);

  // Use custom hook for group share modal management
  const {
    showModal,
    setShowModal,
    shareToken,
    setShareToken,
    handleOpenModal,
    isLoading: isLoadingModal,
  } = useGroupShareModal(selectedConversationIdForShare);

  // Treat absent plan as UNLIMITED — gates pass when no subscription service exists.
  const effectivePlanName = currentPlan?.planCode ?? PLANS.UNLIMITED;

  useEffect(() => {
    if (effectivePlanName !== PLANS.NOPLAN && configurationSettings) {
      fetchSidebarConversations(configurationSettings, { page: individualPage, pageSize, isGroupConversation: false, includeAgentConversations: true});
    }
  }, [fetchSidebarConversations, configurationSettings, individualPage, pageSize, effectivePlanName]);

  useEffect(() => {
    if (effectivePlanName !== PLANS.NOPLAN && configurationSettings) {
      fetchSidebarConversations(configurationSettings, { page: groupPage, pageSize, isGroupConversation: true });
    }
  }, [fetchSidebarConversations, configurationSettings, groupPage, pageSize, effectivePlanName]);

  const sidebarScrollRef = useRef<HTMLDivElement | null>(null);
  const loadMoreLockRef = useRef(false);
  const hasMoreIndividualChatsRef = useRef(hasMoreIndividualChats);
  const hasMoreGroupChatsRef = useRef(hasMoreGroupChats);

  hasMoreIndividualChatsRef.current = hasMoreIndividualChats;
  hasMoreGroupChatsRef.current = hasMoreGroupChats;

  useEffect(() => {
    if (!isSidebarLoading) {
      loadMoreLockRef.current = false;
    }
  }, [isSidebarLoading]);

  const loadMoreIfNearBottom = useCallback(() => {
    const root = sidebarScrollRef.current;
    if (!root || loadMoreLockRef.current) return;
    if (useChatStore.getState().isSidebarLoading) return;
    if (root.scrollHeight <= root.clientHeight + 160) return;
    if (root.scrollHeight - root.scrollTop - root.clientHeight > 160) return;

    if (hasMoreIndividualChatsRef.current) {
      loadMoreLockRef.current = true;
      setIndividualPage((prev) => prev + 1);
      return;
    }

    if (hasMoreGroupChatsRef.current) {
      loadMoreLockRef.current = true;
      setGroupPage((prev) => prev + 1);
    }
  }, []);

  useEffect(() => {
    if (isSidebarLoading) return;
    loadMoreIfNearBottom();
  }, [isSidebarLoading, individualChats.length, groupChats.length, loadMoreIfNearBottom]);

  const createConversationMenuOptions = (
    conversationId: string,
    conversationTitle: string,
    shareTokens: Record<string, string>,
    isGroupChat?: boolean,
  ): modalOption[] => {
    // Get share token from the centralized store (updated in real-time)
    const shareToken = shareTokens[conversationId];
    const hasShareToken = !!shareToken;

    // Get conversation from sidebar list to check CreatedBy
    const conversation = flatConversationList.find((c) => c.ConversationId === conversationId);
    const currentUser = useCommonStore.getState().user;
    const isCreator = conversation?.CreatedBy === currentUser?.id;
    const isAgentChat = isCatalogueAgentId(conversation?.AgentId);
    const isProjectChat = Boolean(conversation?.ProjectId) && !isGroupChat;

    const baseOptions: modalOption[] = [
      {
        id: 'rename',
        name: t('RENAME'),
        icon: 'Pencil' as IconName,
        callback: () => {
          console.log('Rename Conversation:', conversationId);
          setIsRenameDialogOpen(true);
          setNewTitle(conversationTitle || '');
          setConversationState({
            id: conversationId,
            type: 'rename',
          });
        },
      }
    ];

    if (!isProjectChat && !isAgentChat && isGroupChatEnabled && hasValidPlanForStartViaShareLink(currentPlan)) {
      baseOptions.push({
        id: 'start-group-chat',
        name: hasShareToken
          ? (t('ADD_PARTICIPANT_VIA_LINK') || 'Add Participant via link')
          : (t('START_GROUP_CHAT') || 'Start group chat'),
        icon: 'UserRoundPlus' as IconName,
        isDisabled: isLoadingModal,
        callback: () => {
          console.log('Start Group Chat:', conversationId);
          setSelectedConversationIdForShare(conversationId);
          handleOpenModal();
        },
      });
    }

    if (!isProjectChat && !isAgentChat && !isGroupChat) {
      baseOptions.push({
        id: 'add-to-project',
        type: 'submenu' as const,
        name: t('ADD_TO_PROJECT'),
        icon: 'FolderClosed' as IconName,
        subOptions: [
          {
            id: 'new-project',
            name: t('NEW_PROJECT'),
            icon: 'FolderPlus' as IconName,
            callback: () => {
              setIsAddProjectModalOpen(true);
              setConversationIdToAddToProject(conversationId);
            },
          },
          ...sidebarProjectsList.map((project) => ({
            id: project.id,
            name: project.name,
            icon: 'FolderClosed' as IconName,
            callback: () => handleAddToProject(project.id, conversationId),
          })),
        ],
      });
      baseOptions.push({
        id: 'archive',
        name: t('ARCHIVE'),
        icon: 'Archive' as IconName,
        callback: () => {
          console.log('Archive Conversation:', conversationId);
          setIsConfirmDialogOpen(true);
          setConversationState({
            id: conversationId,
            type: 'archive',
          });
        },
      });
    }

    if (isCreator) {
      baseOptions.push({
        id: 'delete',
        name: t('DELETE'),
        icon: 'Trash2' as IconName,
        callback: () => {
          console.log('Delete Conversation:', conversationId);
          setIsConfirmDialogOpen(true);
          setConversationState({
            id: conversationId,
            type: 'delete',
          });
        },
      });
    } else if (!isProjectChat) {
      baseOptions.push({
        id: 'leave',
        name: t('LEAVE') || 'Leave',
        icon: 'LogOut' as IconName,
        callback: () => {
          console.log('Leave Conversation:', conversationId);
          setIsConfirmDialogOpen(true);
          setConversationState({
            id: conversationId,
            type: 'leave',
          });
        },
      });
    }

    return baseOptions;
  };

  const handleAddToProject = async (projectId: string, conversationId: string) => {
    try {
      const toProject = sidebarProjectsList.find((p) => p.id === projectId);
      if (!toProject) return;
      const payload = {
        projectId: projectId,
        conversationId: conversationId,
      };
      await updateProject({ payload: payload, skipInvalidate: true });
      await fetchAndUpdateProjectAllConversations(configurationSettings, projectId);
      // Project chats also belong in Recent Chats — refresh the list so the row
      // stays with updated ProjectId/color instead of disappearing.
      if (configurationSettings) {
        await fetchSidebarConversations(configurationSettings, {
          page: 0,
          pageSize: 50,
          isGroupConversation: false,
          includeAgentConversations: true,
        });
      }
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('MOVE_SUCCESS_DESCRIPTION'),
      });
    } catch {
      toast({
        variant: 'destructive',
        title: t('MOVE_FAILED'),
        description: t('MOVE_FAILED_DESCRIPTION'),
      });
    } finally {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
    }
  };

  const handleRename = async (conversationId: string) => {
    try {
      setIsRenameLoading(true);
      await updateProject({ payload: { conversationId, conversationTitle: newTitle } });
      updateConversationTitleInStore(conversationId, newTitle, { isManual: true });
      setIsRenameDialogOpen(false);
      setIsRenameLoading(false);
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('RENAME_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsRenameDialogOpen(false);
      setIsRenameLoading(false);
      toast({
        variant: 'destructive',
        title: t('RENAME_FAILED'),
        description: t('RENAME_FAILED_DESCRIPTION'),
      });
      console.error('Error renaming conversation:', error);
    }
  };

  const handleArchive = async (conversationId: string) => {
    try {
      setIsLoading(true);
      await updateProject({ payload: { isArchived: true, conversationId } });
      removeConversationFromRecentAndProjects(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      if (conversationIdFromParams && conversationIdFromParams === conversationId)
        navigate('/chat');
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('ARCHIVE_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('ARCHIVE_FAILED'),
        description: t('ARCHIVE_FAILED_DESCRIPTION'),
      });
      console.error('Error archiving conversation:', error);
    }
  };

  const handleDelete = async (conversationId: string) => {
    try {
      setIsLoading(true);
      markConversationDeletedLocally(conversationId);
      await deleteConversationById(conversationId, configurationSettings);
      removeConversationFromRecentAndProjects(conversationId);
      removeAgentConversationFromCache(queryClient, conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      if (conversationIdFromParams && conversationIdFromParams === conversationId)
        navigate('/chat');
      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('DELETE_SUCCESS_DESCRIPTION'),
      });
    } catch (error) {
      clearLocallyDeletedConversation(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('DELETE_FAILED'),
        description: t('DELETE_FAILED_DESCRIPTION'),
      });
      console.error('Error deleting conversation:', error);
    }
  };

  const handleLeave = async (conversationId: string) => {
    try {
      setIsLoading(true);
      const currentUser = useCommonStore.getState().user;
      if (!currentUser?.id || !configurationSettings) {
        throw new Error('User or configuration not available');
      }

      // Get conversation title for the payload
      const conversation = flatConversationList.find((c) => c.ConversationId === conversationId);
      const conversationTitle = conversation?.ConversationTitle || '';

      const payload = {
        invitationType: 'REMOVE_USER' as const,
        userIds: [currentUser.id],
        conversationId: conversationId,
        conversationTitle: conversationTitle,
      } as any; // Type assertion needed as InvitationPayload interface doesn't include userIds and conversationTitle

      await sendInvitation(payload, configurationSettings);
      removeConversationFromRecentAndProjects(conversationId);
      setIsConfirmDialogOpen(false);
      setIsLoading(false);

      if (conversationIdFromParams && conversationIdFromParams === conversationId) {
        navigate('/chat');
      }

      toast({
        variant: 'success',
        title: t('SUCCESS'),
        description: t('LEAVE_SUCCESS_DESCRIPTION') || 'You have left the conversation',
      });
    } catch (error) {
      setIsConfirmDialogOpen(false);
      setIsLoading(false);
      toast({
        variant: 'destructive',
        title: t('LEAVE_FAILED') || 'Failed to leave',
        description: t('LEAVE_FAILED_DESCRIPTION') || 'Failed to leave the conversation',
      });
      console.error('Error leaving conversation:', error);
    }
  };

  const handleConfirmAction = () => {
    if (conversationState.id && conversationState.type === 'archive') {
      handleArchive(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'delete') {
      handleDelete(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'rename') {
      handleRename(conversationState.id);
    } else if (conversationState.id && conversationState.type === 'leave') {
      handleLeave(conversationState.id);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: any) => {
    const conversationId = event.active.id;
    const conversation = flatConversationList.find((c) => c.ConversationId === conversationId);

    setIsDragging(true);
    setActiveConversation({
      id: conversationId,
      title: conversation?.ConversationTitle || t('NEW_CHAT'),
    });
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setIsDragging(false);
    if (over && over.id) {
      const conversationId = active.id;
      const projectId = over.id;

      const project = sidebarProjectsList.find((p) => p.id === projectId);

      toast({
        variant: 'info',
        title: t('MOVING_CONVERSATION'),
        description: `Moving "${activeConversation?.title}" to "${project?.name}"`,
        duration: 2000,
      });

      handleAddToProject(projectId, conversationId);
    }

    setTimeout(() => {
      setActiveConversation(null);
    }, 0);
  };

  const handleDragCancel = () => {
    setIsDragging(false);
    setActiveConversation(null);
    toast({
      title: 'Drag cancelled',
      description: 'Conversation was not moved',
      variant: 'destructive',
      duration: 2000,
    });

    setTimeout(() => {
      setIsDragging(false);
      setActiveConversation(null);
    }, 150);
  };

  const handleDragOver = (event: any) => {
    const { over } = event;
    if (over) {
      const project = sidebarProjectsList.find((p) => p.id === over.id);
      console.log(`Dragging over project: ${project?.name}`);
    }
  };

  const setNewChat = useChatStore((state) => state.setNewChat);
  const disableNewChat = useChatStore((state) => state.disableNewChat);

  const onNewChatClick = () => {
    if (disableNewChat) return;
    if (configureOpen) {
      setConfigureOpen(false);
      if (!isConfigureInline) {
        setOpen(false);
      }
    }
    if (isSidebarOverlay) {
      setOpenMobile(false);
    }
    setNewChat(true);
    setIsIncognitoChat(false);
    if (user?.id) {
      clearUserSelection(user.id);
    }
    navigate({ pathname: '/chat', search: '' });
  };

  const handleSettingsBack = () => {
    setNewChat(true);
    setIsIncognitoChat(false);
    navigate({ pathname: '/chat', search: '' });
  };

  useEffect(() => {
    if (!isConfigureRoute(pathname)) {
      setConfigureOpen(false);
      return;
    }
    if (isMobile) {
      setConfigureOpen(true);
    }
  }, [pathname, isMobile, setConfigureOpen]);

  const showExpandedNav = isSidebarOverlay;
  const [chatsPanelDismissed, setChatsPanelDismissed] = useState(false);
  const [isSidebarPreview, setIsSidebarPreview] = useState(false);
  const isSettingsActive = isSettingsRoute(pathname);
  const showSettingsPanel = !isSidebarOverlay && isSettingsActive;
  const chatsPanelAllowed =
    !isSidebarOverlay &&
    !configureOpen &&
    !isSettingsActive &&
    effectivePlanName !== PLANS.NOPLAN;
  const showChatsPanel = chatsPanelAllowed && !chatsPanelDismissed;
  const hideDesktopRail = !isSidebarOverlay && chatsPanelDismissed && !isSettingsActive;
  const showDesktopSidebar = !hideDesktopRail || isSidebarPreview;
  const showChatsFlyout = showChatsPanel || (chatsPanelAllowed && isSidebarPreview);
  const showChatsInSidebar =
    isSidebarOverlay &&
    !isSettingsActive &&
    effectivePlanName !== PLANS.NOPLAN;
  const showChatLists = showChatsFlyout || showChatsInSidebar;
  const showFullChatLists = showChatLists && hasValidConversationComponentRoute(pathname);

  useLayoutEffect(() => {
    setChatsPanelOpen(showChatsPanel || showSettingsPanel);
  }, [showChatsPanel, showSettingsPanel, setChatsPanelOpen]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.altKey) return;
      if (event.key.toLowerCase() !== 'b' || (!event.metaKey && !event.ctrlKey)) return;
      if (!chatsPanelAllowed) return;
      event.preventDefault();
      setChatsPanelDismissed((dismissed) => !dismissed);
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [chatsPanelAllowed]);

  const onIncognitoClick = () => {
    if (isSidebarOverlay) {
      setOpenMobile(false);
    }
    setNewChat(true);
    setIsIncognitoChat(true);
    navigate('/chat?incognito-chat=true');
  };

  // const scrollToGroupChats = () => {
  //   if (isMobile) {
  //     setOpenMobile(false);
  //   }
  //   document.getElementById('sidebar-group-chats')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // };

  useEffect(() => {
    if (!isSidebarOverlay) {
      setOpenMobile(false);
    }
  }, [pathname, setOpenMobile, isSidebarOverlay]);

  const chatLists = showFullChatLists ? (
            <DndContext
              sensors={sensors}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
              onDragOver={handleDragOver}
              modifiers={[restrictToVerticalAxis]}
            >
              <div
                ref={sidebarScrollRef}
                onScroll={loadMoreIfNearBottom}
                className={
                  agentGroupBy === 'agents'
                    ? 'flex min-h-0 flex-1 flex-col overflow-hidden bg-transparent pr-0.5'
                    : 'min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-transparent [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pr-0.5'
                }
              >
                <div
                  className={
                    agentGroupBy === 'agents'
                      ? 'flex min-h-0 flex-1 flex-col gap-0 px-3 pb-4 pt-0'
                      : 'flex flex-col gap-0 px-3 pb-4 pt-0'
                  }
                >
                  {agentGroupBy === 'agents' && (
                    <div className="shrink-0">
                      <BuddyChatsSection
                        conversations={individualChats}
                        isSidebarLoading={isSidebarIndividualLoading}
                        renderRightAction={(id, title) => (
                          <ConversationContextMenu
                            options={createConversationMenuOptions(id, title, shareTokens, false)}
                          />
                        )}
                      />
                    </div>
                  )}

                  <SidebarGroupedSections
                    agents={agentsData?.agents}
                    chatId={activeSidebarChatId}
                    onGroupByChange={setAgentGroupBy}
                    fill={agentGroupBy === 'agents'}
                  />

                  {isGroupChatEnabled && groupChats?.length > 0 && (
                    <div id="sidebar-group-chats" className="shrink-0">
                      <GroupChatsSection
                        conversations={groupChats}
                        isSidebarLoading={isSidebarGroupLoading}
                        renderRightAction={(id, title) => (
                          <ConversationContextMenu
                            options={createConversationMenuOptions(id, title, shareTokens, true)}
                          />
                        )}
                      />
                    </div>
                  )}

                  {agentGroupBy !== 'agents' && (
                    <IndividualChatsSection
                      conversations={individualChats}
                      agents={agentsData?.agents}
                      isSidebarLoading={isSidebarIndividualLoading}
                      renderRightAction={(id, title) => (
                        <ConversationContextMenu
                          options={createConversationMenuOptions(id, title, shareTokens, false)}
                        />
                      )}
                    />
                  )}
                </div>

                <CustomDialog
                  isOpen={isConfirmDialogOpen}
                  isLoading={isLoading}
                  setIsOpen={setIsConfirmDialogOpen}
                  title={
                    conversationState.type === 'delete'
                      ? t('DELETE')
                      : conversationState.type === 'leave'
                        ? t('LEAVE') || 'Leave'
                        : t('CONFIRM_ACTION')
                  }
                  onConfirm={handleConfirmAction}
                  confirmText={t('YES')}
                  cancelText={t('CANCEL')}
                  description={''}
                >
                  <p>
                    {conversationState.type === 'delete'
                      ? t('DELETE_CONFIRMATION_DESCRIPTION') || t('CONFIRM_ACTION_DESCRIPTION')
                      : conversationState.type === 'leave'
                        ? t('LEAVE_CONFIRMATION_DESCRIPTION') || 'Are you sure you want to leave this conversation?'
                        : t('CONFIRM_ACTION_DESCRIPTION')}
                  </p>
                </CustomDialog>

                <CustomDialog
                  isOpen={isRenameDialogOpen}
                  isLoading={isRenameLoading}
                  setIsOpen={setIsRenameDialogOpen}
                  title={t('RENAME')}
                  onConfirm={handleConfirmAction}
                  confirmText={t('SAVE')}
                  cancelText={t('CANCEL')}
                  description={''}
                  isConfirmDisabled={newTitle.trim() === ''}
                >
                  <DebouncedInput
                    placeholder={t('ENTER_NEW_TITLE')}
                    initialValue={newTitle}
                    onDebouncedChange={setNewTitle}
                    className="
                            h-10 w-full rounded-3xl
                            border border-primary
                            bg-background pr-8
                            focus:outline-none
                            focus:border-primary
                            focus:ring-1 focus:ring-primary
                 "
                  />
                </CustomDialog>

                <NewProjectModal
                  callback={() => {
                    refetchProject(queryClient, '');
                    if (configurationSettings) {
                      void fetchSidebarConversations(configurationSettings, {
                        page: 0,
                        pageSize: 50,
                        isGroupConversation: false,
                        includeAgentConversations: true,
                      });
                    }
                  }}
                  newConversatioId={conversationIdToAddToProject}
                  isOpen={isAddProjectModalOpen}
                  onClose={() => setIsAddProjectModalOpen(false)}
                />

                {/* DragOverlay for visual feedback */}
                <DragOverlay modifiers={[restrictToVerticalAxis]}>
                  {isDragging && activeConversation ? (
                    <div className="bg-primary-25 rounded-lg px-3 py-2 shadow-lg border border-primary z-[9999]">
                      <p className={`text-sm truncate max-w-[200px] ${theme.theme === 'dark' ? 'text-true-white' : 'text-foreground'}`}>
                        📄 {activeConversation.title}
                      </p>
                    </div>
                  ) : null}
                </DragOverlay>
              </div>
            </DndContext>
          ) : showChatLists ? (
            <div
              className={
                agentGroupBy === 'agents'
                  ? 'flex min-h-0 flex-1 flex-col overflow-hidden bg-transparent pr-0.5'
                  : 'min-h-0 flex-1 overflow-x-hidden overflow-y-auto bg-transparent [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pr-0.5'
              }
            >
              <div
                className={
                  agentGroupBy === 'agents'
                    ? 'flex min-h-0 flex-1 flex-col gap-0 px-3 pb-4 pt-0'
                    : 'flex flex-col gap-0 px-3 pb-4 pt-0'
                }
              >
                <SidebarGroupedSections
                  agents={agentsData?.agents}
                  chatId={activeSidebarChatId}
                  onGroupByChange={setAgentGroupBy}
                />
              </div>
            </div>
          ) : null;

  const sidebarStyle = !isSidebarOverlay
    ? ({
      width: '61px',
      minWidth: '61px',
      height: '100%',
      borderRight: '0px solid transparent',
    } as React.CSSProperties)
    : undefined;

  const sidebarContent = (
    <Sidebar
      className={cn(
        'bg-sidebar-surface dark:bg-sidebar h-full border-grey-100 dark:border-sidebar-border',
        (showChatsPanel || (configureOpen && !isConfigureInline)) ? 'border-r-0' : 'border-r'
      )}
      collapsible="icon"
      style={sidebarStyle}
    >
      <div
        className={cn(
          'flex h-full min-h-0 flex-col gap-6 bg-sidebar-surface pb-6 dark:bg-transparent',
          // The collapsed toggle sits in the top 56px. Home starts below it.
          isSidebarPreview ? 'pt-16' : 'pt-4',
          // 61px rail = 6px + 48px nav column + 6px + the inner panel's 1px right border.
          isSidebarOverlay ? 'px-2' : 'px-1.5'
        )}
        data-testid="app-sidebar-rail"
      >
        {type === SidebarHeaderVariant.Logo && (
          <SidebarHeader className="!p-0 shrink-0">
            <LogoSidebarHeader
              type={type}
              logo={logo}
              smallLogo={smallLogo}
              open={false}
              isMobile={isSidebarOverlay}
              setOpenMobile={setOpenMobile}
            />
          </SidebarHeader>
        )}

        <SidebarContent className="text-base mr-0 min-h-0 flex-1 text-high-emphasis font-normal group-data-[collapsible=icon]:ml-0 group-data-[collapsible=icon]:pr-0 !overflow-hidden flex flex-col">
          {/* Phone/tablet: Settings replaces the main nav, with Back to return. */}
          {isSidebarOverlay && isSettingsActive ? (
            <SidebarSettingsPanel onBack={handleSettingsBack} />
          ) : (
            <div
              // Phone/tablet: room between Settings and the chat sections below it.
              className={cn('flex flex-shrink-0 flex-col gap-2.5 bg-transparent', isSidebarOverlay && 'pb-6')}
            >
              <SidebarPrimaryNavItem
                label={t('HOME', { defaultValue: 'Home' })}
                icon="new-chat"
                to={{ pathname: '/chat', search: '' }}
                onClick={onNewChatClick}
                isActive={isPathActive(pathname, '/chat')}
                disabled={disableNewChat}
                showText={showExpandedNav}
              />
              <SidebarPrimaryNavItem
                label={t('AGENTS')}
                icon="agents"
                to="/agents"
                isActive={isPathActive(pathname, '/agents') || pathname.startsWith('/agent-details')}
                showText={showExpandedNav}
                onClick={() => {
                  setNewChat(false);
                  if (isSidebarOverlay) setOpenMobile(false);
                }}
              />
              <SidebarPrimaryNavItem
                label={t('PROJECTS_TITLE', { defaultValue: 'Projects' })}
                icon="projects"
                to="/projects"
                isActive={isPathActive(pathname, '/projects') || pathname.startsWith('/project/')}
                showText={showExpandedNav}
                onClick={() => {
                  setNewChat(false);
                  if (isSidebarOverlay) setOpenMobile(false);
                }}
              />
              {effectivePlanName !== PLANS.NOPLAN && (
                <SidebarPrimaryNavItem
                  label={t('MEDIA_LIBRARY')}
                  icon="library"
                  to="/media-library"
                  isActive={isPathActive(pathname, '/media-library')}
                  showText={showExpandedNav}
                  onClick={() => {
                    setNewChat(false);
                    if (isSidebarOverlay) setOpenMobile(false);
                  }}
                />
              )}
              {isDashboardEnabled && (
                <SidebarPrimaryNavItem
                  label={t('DASHBOARD')}
                  icon="dashboard"
                  to="/dashboard"
                  isActive={isPathActive(pathname, '/dashboard')}
                  showText={showExpandedNav}
                  onClick={() => {
                    setNewChat(false);
                    if (isSidebarOverlay) setOpenMobile(false);
                  }}
                />
              )}
              <SidebarPrimaryNavItem
                label={t('SCHEDULED') || 'Scheduled'}
                icon="scheduled"
                to="/scheduler"
                isActive={isPathActive(pathname, '/scheduler')}
                showText={showExpandedNav}
                onClick={() => {
                  setNewChat(false);
                  if (isSidebarOverlay) setOpenMobile(false);
                }}
              />
              <SidebarPrimaryNavItem
                label={t('SETTINGS', { defaultValue: 'Settings' })}
                icon="settings"
                to={SETTINGS_ACCOUNT_PATH}
                isActive={isSettingsActive}
                showText={showExpandedNav}
                onClick={() => setNewChat(false)}
              />
            </div>
          )}
          {showChatsInSidebar ? chatLists : null}
          {showChatsInSidebar ? <SidebarConfigurePanel /> : null}
        </SidebarContent>

        <AppSidebarFooter />
      </div>

      {isGroupChatEnabled && (
        <GroupShareModal
          showModal={showModal}
          setShowModal={setShowModal}
          shareToken={shareToken}
          setShareToken={setShareToken}
          isLoadingConversation={isLoadingModal}
        />
      )}
    </Sidebar>
  );

  return (
    <>
      <div
        onMouseEnter={() => {
          if (hideDesktopRail) setIsSidebarPreview(true);
        }}
        onMouseLeave={() => setIsSidebarPreview(false)}
      >
        {showDesktopSidebar ? sidebarContent : null}
        {chatsPanelAllowed && chatsPanelDismissed ? (
          <div className="fixed left-1.5 top-4 z-40 hidden lg:block">
            <ShowChatsPanelButton
              onShow={() => {
                setIsSidebarPreview(false);
                setChatsPanelDismissed(false);
              }}
            />
          </div>
        ) : null}
        {showSettingsPanel && (
          <div className="fixed inset-y-0 z-[35] hidden lg:block" style={{ left: 'var(--sidebar-icon-rail-width)' }}>
            <SidebarSettingsPanel onBack={handleSettingsBack} />
          </div>
        )}
        {showChatsFlyout && (
          <div
            className={cn(
              'fixed inset-y-0 z-[35] hidden lg:block',
              isSidebarPreview && chatsPanelDismissed && 'shadow-primary-medium'
            )}
            style={{ left: 'var(--sidebar-icon-rail-width)' }}
          >
            <SidebarChatsPanel
              groupBy={agentGroupBy}
              showHideTooltip={!isSidebarPreview}
              onHide={() => setChatsPanelDismissed(true)}
            >
              {chatLists}
            </SidebarChatsPanel>
          </div>
        )}
      </div>
      <ProjectActionDialogs />
    </>
  );
}
